import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { exportVulnerabilityReportPdf } from '../lib/vulnerabilityReportPdf';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { fmtTs, getTz } from '../lib/globalTz';

interface AttackTech { id: string; name: string; tactic: string }
type AttackMap = Record<string, AttackTech[]>;

interface PatchTuesdayInfo {
  label: string;
  windowStart: string;
  records: ExposureRow[];
  uniqueCves: number;
  devices: number;
}

function currentPatchTuesdayInfo(rows: ExposureRow[]): PatchTuesdayInfo | null {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const first = new Date(Date.UTC(year, month, 1));
  const firstDow = first.getUTCDay();
  const offsetToFirstTuesday = (2 - firstDow + 7) % 7;
  let dayOfMonth = 1 + offsetToFirstTuesday + 7;
  let pt = new Date(Date.UTC(year, month, dayOfMonth));
  if (pt > now) {
    const py = month === 0 ? year - 1 : year;
    const pm = month === 0 ? 11 : month - 1;
    const pf = new Date(Date.UTC(py, pm, 1));
    const pfd = pf.getUTCDay();
    const offset = (2 - pfd + 7) % 7;
    dayOfMonth = 1 + offset + 7;
    pt = new Date(Date.UTC(py, pm, dayOfMonth));
  }
  const ptIso = pt.toISOString().slice(0, 10);
  const records = rows.filter((r) => r.publishedDate && r.publishedDate.slice(0, 10) >= ptIso);
  if (records.length === 0) return null;
  const cves = new Set(records.map((r) => r.cveId).filter(Boolean));
  const devs = new Set(records.map((r) => r.deviceName).filter(Boolean));
  const monthName = pt.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return {
    label: `${monthName} (released ${ptIso})`,
    windowStart: ptIso,
    records,
    uniqueCves: cves.size,
    devices: devs.size,
  };
}

type Severity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Unknown';
type ExploitLevel = 'ExploitIsInKit' | 'ExploitIsVerified' | 'ExploitIsPublic' | 'NoExploit';

interface ExposureRow {
  cveId: string;
  deviceName: string;
  deviceId: string;
  osPlatform: string;
  rbacGroup: string;
  severity: Severity;
  cvssScore: number;
  exploitLevel: ExploitLevel;
  isKEV: boolean;
  pocUrl: string;
  softwareVendor: string;
  softwareName: string;
  softwareVersion: string;
  recommendedUpdate: string;
  recommendedUpdateUrl: string;
  fixingKbId: string;
  publishedDate: string;
  description: string;
}

interface VulnerabilityCacheMeta {
  source: 'cache' | 'live';
  fetchedAt: string;
  expiresAt: string;
  ttlSeconds: number;
}

interface ExposureResponse {
  customerId: string;
  customerName?: string;
  defenderTenantId?: string;
  defenderAuthMode?: string;
  generatedAt: string;
  recordCount: number;
  truncated: boolean;
  records: ExposureRow[];
  warnings: string[];
  cache?: VulnerabilityCacheMeta;
}

interface TopRecommendation {
  id: string; title: string; product: string; vendor: string;
  exposedMachines: number; severityScore: number;
  remediationType: string; status: string; firstDetected: string | null;
}
interface TopSoftware {
  id: string; name: string; vendor: string;
  weaknesses: number; exposedMachines: number; installations: number;
  endOfSupportStatus: string; publicExploit: boolean;
}
interface TopThreat {
  cveId: string; name: string; severity: string; cvss: number;
  exploitInWild: boolean; publicExploit: boolean; exploitTypes: string[];
  exposedMachines: number; publishedOn: string | null; description: string;
}

interface SummaryResponse {
  customerId: string;
  customerName?: string;
  defenderTenantId?: string;
  defenderAuthMode?: string;
  generatedAt: string;
  executive: {
    exposureScore: number | null;
    exposureScoreTime?: string | null;
    secureScore: number | null;
    secureScoreTime?: string | null;
    criticalCves?: number;
    internetFacingDevices?: number;
    activeExploits?: number;
    slaBreaches?: number;
  };
  severity?: { Critical: number; High: number; Medium: number; Low: number };
  devices: {
    total: number;
    vulnerable?: number;
    unhealthy?: number;
    missingPatches?: number;
    osDistribution?: { os: string; count: number }[];
  };
  patches?: {
    missingKBs: number;
    deploymentSuccessPct: number | null;
    avgRemediationDays: number | null;
    unsupportedOs: number;
    topRecommendations: TopRecommendation[];
  };
  software?: {
    eolCount: number;
    vulnerableApps: number;
    browserVulns: number;
    thirdPartyExposure: number;
    topSoftware: TopSoftware[];
  };
  threatIntel?: {
    activeExploitCves: number;
    publicExploitCves: number;
    ransomwareLinkedCves: number;
    ransomwareLinkedProducts: number;
    topThreats: TopThreat[];
  };
  warnings: string[];
  cache?: VulnerabilityCacheMeta;
}

type SubTab = 'executive' | 'severity' | 'devices' | 'patch' | 'software' | 'zeroday';
const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'executive', label: 'Executive' },
  { id: 'severity', label: 'Severity' },
  { id: 'devices', label: 'Devices' },
  { id: 'patch', label: 'Patch Compliance' },
  { id: 'software', label: 'Software Risk' },
  { id: 'zeroday', label: 'Zero-Day & TI' },
];

type VmSubNavLayout = 'horizontal' | 'hamburger';
type DrilldownKind = 'exposure' | 'recommendation' | 'software' | 'threat' | 'device' | 'summary';

interface DrilldownPayload {
  title: string;
  subtitle?: string;
  kind: DrilldownKind;
  metrics: Record<string, string | number>;
  rows: Array<Record<string, unknown>>;
  focus: string;
}

const VM_MEMORY_CACHE = new Map<string, { summary: SummaryResponse; exposure: ExposureResponse; cachedAtMs: number }>();
const VM_MEMORY_CACHE_MS = 5 * 60 * 1000;
const VM_NAV_LAYOUT_KEY = 'kloudsecure:vulnerability-management:subnav-layout';
const VM_CARD_EXPORT_MAX_ROWS = 10000;

function safeDownloadFileName(value: string): string {
  const base = value
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);
  return base || 'vulnerability-card-export';
}

function readContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try { return decodeURIComponent(utf[1].replace(/["']/g, '')); } catch { return utf[1].replace(/["']/g, ''); }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ? plain[1] : null;
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => window.URL.revokeObjectURL(url), 500);
  }
}

const SEV_ORD: Record<Severity, number> = { Critical: 0, High: 1, Medium: 2, Low: 3, Unknown: 4 };
const SEV_PILL: Record<Severity, string> = {
  Critical: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200',
  Low: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200',
  Unknown: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300',
};
const SEV_BAR: Record<Exclude<Severity, 'Unknown'>, string> = {
  Critical: 'bg-red-500',
  High: 'bg-orange-500',
  Medium: 'bg-amber-400',
  Low: 'bg-green-500',
};
const EXP_LBL: Record<ExploitLevel, string> = {
  ExploitIsInKit: 'In Kit',
  ExploitIsVerified: 'Verified',
  ExploitIsPublic: 'Public',
  NoExploit: 'No Exploit',
};

function defenderAuthModeLabel(mode?: string): string {
  if (mode === 'customer_tenant_authority_mssp_app') return 'Customer tenant token';
  if (mode === 'customer_direct_app') return 'Customer app credentials';
  return mode || 'Defender auth pending';
}

interface RemediationGroup {
  key: string;
  vendor: string;
  software: string;
  update: string;
  rows: ExposureRow[];
  deviceCount: number;
  cveCount: number;
  exploits: number;
  kits: number;
  topSev: Severity;
  avgCvss: number;
  latestDate: string;
}

function buildGroups(data: ExposureRow[]): RemediationGroup[] {
  const map = new Map<string, {
    vendor: string; software: string; update: string;
    rows: ExposureRow[]; devs: Set<string>; cves: Set<string>;
    exploits: number; kits: number; cvss: number[]; dates: string[];
  }>();
  for (const r of data) {
    const key = `${r.softwareVendor || '-'}||${r.softwareName || '-'}||${r.recommendedUpdate || '-'}`;
    let g = map.get(key);
    if (!g) {
      g = {
        vendor: r.softwareVendor, software: r.softwareName, update: r.recommendedUpdate,
        rows: [], devs: new Set(), cves: new Set(),
        exploits: 0, kits: 0, cvss: [], dates: [],
      };
      map.set(key, g);
    }
    g.rows.push(r);
    if (r.deviceName) g.devs.add(r.deviceName);
    if (r.cveId) g.cves.add(r.cveId);
    if (r.exploitLevel === 'ExploitIsVerified' || r.exploitLevel === 'ExploitIsPublic') g.exploits++;
    if (r.exploitLevel === 'ExploitIsInKit') g.kits++;
    if (r.cvssScore) g.cvss.push(r.cvssScore);
    if (r.publishedDate) g.dates.push(r.publishedDate);
  }
  return Array.from(map.entries()).map(([key, g]) => {
    let topSev: Severity = 'Unknown';
    for (const row of g.rows) {
      if (SEV_ORD[row.severity] < SEV_ORD[topSev]) topSev = row.severity;
    }
    return {
      key,
      vendor: g.vendor,
      software: g.software,
      update: g.update,
      rows: g.rows,
      deviceCount: g.devs.size,
      cveCount: g.cves.size,
      exploits: g.exploits,
      kits: g.kits,
      topSev,
      avgCvss: g.cvss.length ? +(g.cvss.reduce((a, b) => a + b, 0) / g.cvss.length).toFixed(1) : 0,
      latestDate: g.dates.length ? g.dates.slice().sort().at(-1)! : '',
    };
  });
}

interface DeviceStat {
  name: string;
  cveCount: number;
  Critical: number;
  High: number;
  Medium: number;
  Low: number;
  total: number;
}

function buildTopDevices(data: ExposureRow[], n: number): DeviceStat[] {
  const dm = new Map<string, { name: string; cves: Set<string>; Critical: Set<string>; High: Set<string>; Medium: Set<string>; Low: Set<string> }>();
  for (const r of data) {
    if (!r.deviceName) continue;
    let d = dm.get(r.deviceName);
    if (!d) {
      d = { name: r.deviceName, cves: new Set(), Critical: new Set(), High: new Set(), Medium: new Set(), Low: new Set() };
      dm.set(r.deviceName, d);
    }
    if (r.cveId) d.cves.add(r.cveId);
    if (r.severity !== 'Unknown' && r.cveId) d[r.severity].add(r.cveId);
  }
  return Array.from(dm.values())
    .map((d) => {
      const c = d.Critical.size, h = d.High.size, m = d.Medium.size, l = d.Low.size;
      return { name: d.name, cveCount: d.cves.size, Critical: c, High: h, Medium: m, Low: l, total: c + h + m + l };
    })
    .sort((a, b) => b.cveCount - a.cveCount)
    .slice(0, n);
}

type SortCol = 'update' | 'deviceCount' | 'cveCount' | 'exploits' | 'kits' | 'topSev' | 'avgCvss' | 'latestDate';

export function VulnerabilityManagementTab() {
  const { activeCustomerId, customers, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const canRefresh = canCapability('vulnerability_mgmt', 'refresh');
  const canGenerateBrief = canCapability('vulnerability_mgmt', 'generate_ai_brief');
  const canMapAttack = canCapability('vulnerability_mgmt', 'map_attack_techniques');
  const canUseWhatIf = canCapability('vulnerability_mgmt', 'use_what_if');
  const canExportPdf = canCapability('vulnerability_mgmt', 'export_pdf');
  const canExportCardCsv = canCapability('vulnerability_mgmt', 'export_card_csv');
  const canAnalyzeDrilldown = canCapability('vulnerability_mgmt', 'analyze_drilldown') || canGenerateBrief;
  const canViewPatchCompliance = canCapability('vulnerability_mgmt', 'view_patch_compliance');
  const canViewSoftwareRisk = canCapability('vulnerability_mgmt', 'view_software_risk');
  const canViewZeroDayTi = canCapability('vulnerability_mgmt', 'view_zero_day_ti');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => activeCustomerId && activeCustomerId !== 'default' ? activeCustomerId : '');
  const selectableCustomers = useMemo(() => customers.filter((c) => c.enabled !== false && c.id !== 'default'), [customers]);
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId),
    [customers, selectedCustomerId],
  );
  const selectedCustomerName = selectedCustomer?.name ?? 'Customer';

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [exposure, setExposure] = useState<ExposureResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sevF, setSevF] = useState('');
  const [expF, setExpF] = useState('');
  const [osF, setOsF] = useState('');
  const [grpF, setGrpF] = useState('');
  const [patchTuesdayOnly, setPatchTuesdayOnly] = useState(false);
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [openGroup, setOpenGroup] = useState<RemediationGroup | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownPayload | null>(null);
  const [drilldownAnalysis, setDrilldownAnalysis] = useState<string | null>(null);
  const [drilldownBusy, setDrilldownBusy] = useState(false);
  const [drilldownCsvBusy, setDrilldownCsvBusy] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);

  // AI brief
  const [aiBrief, setAiBrief] = useState<string | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefCollapsed, setBriefCollapsed] = useState(false);

  // What-if simulator
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [whatIfPlan, setWhatIfPlan] = useState<Set<string>>(new Set());

  // ATT&CK technique cache, keyed by CVE id (uppercased)
  const [attackMap, setAttackMap] = useState<AttackMap>({});

  // PDF export
  const [pdfBusy, setPdfBusy] = useState(false);

  // Sub-tab navigation
  const [subTab, setSubTab] = useState<SubTab>('executive');
  const [navLayout, setNavLayoutState] = useState<VmSubNavLayout>(() => {
    if (typeof window === 'undefined') return 'horizontal';
    return window.localStorage.getItem(VM_NAV_LAYOUT_KEY) === 'hamburger' ? 'hamburger' : 'horizontal';
  });
  const [navCollapsed, setNavCollapsed] = useState(true);

  const setNavLayout = useCallback((layout: VmSubNavLayout) => {
    setNavLayoutState(layout);
    if (typeof window !== 'undefined') window.localStorage.setItem(VM_NAV_LAYOUT_KEY, layout);
    setNavCollapsed(layout === 'hamburger');
  }, []);

  const visibleSubTabs = useMemo(() => SUB_TABS.filter((tab) => {
    if (tab.id === 'patch') return canViewPatchCompliance;
    if (tab.id === 'software') return canViewSoftwareRisk;
    if (tab.id === 'zeroday') return canViewZeroDayTi;
    return true;
  }), [canViewPatchCompliance, canViewSoftwareRisk, canViewZeroDayTi]);

  useEffect(() => {
    if (!visibleSubTabs.some((tab) => tab.id === subTab)) {
      setSubTab(visibleSubTabs[0]?.id ?? 'executive');
    }
  }, [visibleSubTabs, subTab]);

  useEffect(() => {
    const activeIsSelectable = activeCustomerId && activeCustomerId !== 'default' && selectableCustomers.some((c) => c.id === activeCustomerId);
    const selectedIsSelectable = selectedCustomerId && selectableCustomers.some((c) => c.id === selectedCustomerId);
    if (activeIsSelectable && activeCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(activeCustomerId);
      return;
    }
    if (!selectedIsSelectable) {
      setSelectedCustomerId(activeIsSelectable ? activeCustomerId : (selectableCustomers[0]?.id ?? ''));
    }
  }, [activeCustomerId, selectableCustomers, selectedCustomerId]);

  const handleSelectedCustomerChange = useCallback((id: string) => {
    setSelectedCustomerId(id);
    if (id && id !== activeCustomerId) setActiveCustomerId(id);
  }, [activeCustomerId, setActiveCustomerId]);

  // Reset per-tenant feature state whenever the active customer changes so a
  // brief, attack-map cache, or simulator plan from tenant A never leaks into B.
  useEffect(() => {
    setAiBrief(null);
    setBriefError(null);
    setBriefCollapsed(false);
    setAttackMap({});
    setWhatIfPlan(new Set());
    setPatchTuesdayOnly(false);
    setWhatIfOpen(false);
    setDrilldown(null);
    setDrilldownAnalysis(null);
    setDrilldownError(null);
    setDrilldownCsvBusy(false);
    setSubTab('executive');
  }, [selectedCustomerId]);

  const load = useCallback(async (forceRefresh = false) => {
    if (!selectedCustomerId || selectedCustomerId === 'default') {
      setSummary(null);
      setExposure(null);
      setError(null);
      return;
    }

    const memory = VM_MEMORY_CACHE.get(selectedCustomerId);
    if (!forceRefresh && memory && Date.now() - memory.cachedAtMs < VM_MEMORY_CACHE_MS) {
      setSummary(memory.summary);
      setExposure(memory.exposure);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    if (forceRefresh) {
      setSummary(null);
      setExposure(null);
    }
    try {
      const cid = encodeURIComponent(selectedCustomerId);
      const refresh = forceRefresh ? '&refresh=1' : '';
      const [s, e] = await Promise.all([
        apiFetch<SummaryResponse>(`/vulnerability/summary?customerId=${cid}${refresh}`),
        apiFetch<ExposureResponse>(`/vulnerability/exposure-records?customerId=${cid}${refresh}`),
      ]);
      setSummary(s);
      setExposure(e);
      VM_MEMORY_CACHE.set(selectedCustomerId, { summary: s, exposure: e, cachedAtMs: Date.now() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vulnerability data');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId]);

  useEffect(() => { void load(false); }, [load]);

  const raw = exposure?.records ?? [];

  const osOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of raw) if (r.osPlatform) s.add(r.osPlatform);
    return Array.from(s).sort();
  }, [raw]);
  const grpOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of raw) if (r.rbacGroup) s.add(r.rbacGroup);
    return Array.from(s).sort();
  }, [raw]);

  const patchTuesday = useMemo(() => currentPatchTuesdayInfo(raw), [raw]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const ptStart = patchTuesday?.windowStart ?? '';
    return raw.filter((r) => {
      if (sevF && r.severity !== sevF) return false;
      if (expF && r.exploitLevel !== expF) return false;
      if (osF && r.osPlatform !== osF) return false;
      if (grpF && r.rbacGroup !== grpF) return false;
      if (patchTuesdayOnly && ptStart) {
        if (!r.publishedDate || r.publishedDate.slice(0, 10) < ptStart) return false;
      }
      if (q) {
        const hay = [r.cveId, r.deviceName, r.softwareName, r.softwareVendor, r.rbacGroup, r.description].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [raw, search, sevF, expF, osF, grpF, patchTuesdayOnly, patchTuesday]);

  const isFilterActive = !!(search || sevF || expF || osF || grpF || patchTuesdayOnly);

  const groups = useMemo(() => {
    const g = buildGroups(filtered);
    if (sortCol) {
      g.sort((a, b) => {
        let av: number | string = a[sortCol] ?? 0;
        let bv: number | string = b[sortCol] ?? 0;
        if (sortCol === 'topSev') { av = SEV_ORD[a.topSev]; bv = SEV_ORD[b.topSev]; }
        if (sortCol === 'update') {
          av = (a.update || a.software || '').toLowerCase();
          bv = (b.update || b.software || '').toLowerCase();
        }
        if (typeof av === 'string') return sortAsc ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
        return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
      });
    }
    return g;
  }, [filtered, sortCol, sortAsc]);

  const topDevices = useMemo(() => buildTopDevices(filtered, 10), [filtered]);
  const missingByDev = useMemo(() => buildTopDevices(filtered, 15), [filtered]);

  // KPI numbers driven off the full RAW set (not filtered) so the executive
  // summary stays stable while the analyst narrows the table.
  const kpi = useMemo(() => {
    const sev = { Critical: 0, High: 0, Medium: 0, Low: 0, Unknown: 0 } as Record<Severity, number>;
    const devices = new Set<string>();
    let exploitable = 0;
    for (const r of raw) {
      sev[r.severity]++;
      if (r.deviceName) devices.add(r.deviceName);
      if (r.exploitLevel === 'ExploitIsVerified' || r.exploitLevel === 'ExploitIsPublic' || r.exploitLevel === 'ExploitIsInKit') exploitable++;
    }
    return {
      activeDevices: summary?.devices.total ?? devices.size,
      totalRecords: raw.length,
      critical: sev.Critical,
      high: sev.High,
      medium: sev.Medium,
      low: sev.Low,
      exploitable,
    };
  }, [raw, summary]);

  const resetFilters = () => {
    setSearch(''); setSevF(''); setExpF(''); setOsF(''); setGrpF(''); setPatchTuesdayOnly(false);
  };

  const generateBrief = useCallback(async () => {
    if (!selectedCustomerId || selectedCustomerId === 'default' || !canGenerateBrief) return;
    setBriefLoading(true);
    setBriefError(null);
    try {
      const top = [...raw]
        .sort((a, b) => (b.cvssScore - a.cvssScore) || (Number(b.isKEV) - Number(a.isKEV)))
        .slice(0, 15)
        .map((r) => ({
          cveId: r.cveId,
          severity: r.severity,
          cvssScore: r.cvssScore,
          exploitLevel: r.exploitLevel,
          isKEV: r.isKEV,
          deviceCount: raw.filter((x) => x.cveId === r.cveId).length,
          softwareVendor: r.softwareVendor,
          softwareName: r.softwareName,
          description: r.description,
        }));
      const data = await apiFetch<{ ok: boolean; markdown?: string; error?: string }>(
        `/vulnerability/ai-brief?customerId=${encodeURIComponent(selectedCustomerId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kpi,
            topCves: top,
            exposureScore: summary?.executive?.exposureScore ?? null,
            secureScore: summary?.executive?.secureScore ?? null,
            patchTuesdayCount: patchTuesday?.uniqueCves ?? 0,
          }),
        },
      );
      if (!data.ok || !data.markdown) throw new Error(data.error || 'AI brief unavailable');
      setAiBrief(data.markdown);
    } catch (e) {
      setBriefError((e as Error).message);
    } finally {
      setBriefLoading(false);
    }
  }, [selectedCustomerId, raw, kpi, summary, patchTuesday, canGenerateBrief]);

  const fetchAttackMap = useCallback(async (cves: { cveId: string; description?: string; software?: string }[]) => {
    if (!selectedCustomerId || selectedCustomerId === 'default' || !canMapAttack || cves.length === 0) return;
    const needed = cves.filter((c) => !attackMap[c.cveId.toUpperCase()]);
    if (needed.length === 0) return;
    try {
      const data = await apiFetch<{ ok: boolean; map?: AttackMap; error?: string }>(
        `/vulnerability/attack-map?customerId=${encodeURIComponent(selectedCustomerId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cves: needed.slice(0, 50) }),
        },
      );
      if (!data.ok || !data.map) throw new Error(data.error || 'attack-map unavailable');
      setAttackMap((prev) => ({ ...prev, ...(data.map as AttackMap) }));
    } catch (e) {
      console.error('[attack-map]', e);
    }
  }, [selectedCustomerId, attackMap, canMapAttack]);

  const computeWhatIfPlan = useCallback(() => {
    if (whatIfPlan.size === 0) return null;
    const selectedGroups = groups.filter((g) => whatIfPlan.has(g.key));
    const removedPairs = new Set<string>();
    const criticalPairs = new Set<string>();
    const removedDevices = new Set<string>();
    const removedCves = new Set<string>();
    for (const g of selectedGroups) {
      for (const r of g.rows) {
        const pair = `${r.deviceName}|${r.cveId}`;
        removedPairs.add(pair);
        removedDevices.add(r.deviceName);
        removedCves.add(r.cveId);
        if (r.severity === 'Critical') criticalPairs.add(pair);
      }
    }
    const criticalRemoved = criticalPairs.size;
    const stillExposedDevices = new Set<string>();
    for (const r of raw) {
      const key = `${r.deviceName}|${r.cveId}`;
      if (!removedPairs.has(key)) stillExposedDevices.add(r.deviceName);
    }
    return {
      items: selectedGroups.map((g) => ({
        update: g.update,
        devices: g.deviceCount,
        cves: g.cveCount,
      })),
      devicesRemoved: Array.from(removedDevices).filter((d) => !stillExposedDevices.has(d)).length,
      cvesRemoved: removedCves.size,
      criticalRemoved,
    };
  }, [whatIfPlan, groups, raw]);

  const exportPdf = useCallback(async () => {
    if (!exposure || !canExportPdf) return;
    setPdfBusy(true);
    try {
      const topDevAgg = topDevices.slice(0, 10).map((d) => ({ name: d.name, cveCount: d.cveCount }));
      const topCveAgg = [...raw]
        .sort((a, b) => (b.cvssScore - a.cvssScore) || (Number(b.isKEV) - Number(a.isKEV)))
        .slice(0, 20)
        .map((r) => ({
          cveId: r.cveId,
          severity: r.severity,
          cvssScore: r.cvssScore,
          deviceCount: raw.filter((x) => x.cveId === r.cveId).length,
          exploit: r.exploitLevel,
          kev: r.isKEV,
          software: `${r.softwareVendor ?? ''} ${r.softwareName ?? ''}`.trim(),
        }));
      const topRem = groups.slice(0, 15).map((g) => ({
        update: g.update,
        vendor: g.vendor,
        devices: g.deviceCount,
        cves: g.cveCount,
        topSeverity: g.topSev,
        avgCvss: g.avgCvss,
      }));
      await exportVulnerabilityReportPdf({
        customerName: selectedCustomerName,
        generatedAt: new Date().toISOString(),
        tz: getTz(),
        kpi,
        exposureScore: summary?.executive?.exposureScore ?? null,
        secureScore: summary?.executive?.secureScore ?? null,
        aiBriefMarkdown: aiBrief,
        patchTuesday: patchTuesday ? {
          label: patchTuesday.label,
          count: patchTuesday.records.length,
          uniqueCves: patchTuesday.uniqueCves,
          devices: patchTuesday.devices,
        } : null,
        topDevices: topDevAgg,
        topRemediations: topRem,
        topCves: topCveAgg,
        whatIfPlan: computeWhatIfPlan(),
      });
    } catch (e) {
      setError(`PDF export failed: ${(e as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  }, [exposure, raw, topDevices, groups, kpi, summary, aiBrief, patchTuesday, computeWhatIfPlan, selectedCustomerName, canExportPdf]);

  const asRecordRows = useCallback((rows: unknown[], max = VM_CARD_EXPORT_MAX_ROWS): Array<Record<string, unknown>> => (
    rows.slice(0, max).map((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return { value: String(row ?? '') };
      const source = row as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(source)) {
        if (typeof value === 'string') out[key] = value.length > 2000 ? `${value.slice(0, 2000)}...` : value;
        else if (typeof value === 'number' || typeof value === 'boolean' || value == null) out[key] = value;
      }
      return out;
    })
  ), []);

  const openDrilldown = useCallback((payload: DrilldownPayload) => {
    setDrilldown(payload);
    setDrilldownAnalysis(null);
    setDrilldownError(null);
  }, []);

  const openExposureDrilldown = useCallback((title: string, subtitle: string, rows: ExposureRow[], focus: string, extraMetrics: Record<string, string | number> = {}) => {
    const devices = new Set(rows.map((r) => r.deviceName).filter(Boolean));
    const cves = new Set(rows.map((r) => r.cveId).filter(Boolean));
    openDrilldown({
      title,
      subtitle,
      kind: 'exposure',
      focus,
      metrics: {
        Records: rows.length,
        Devices: devices.size,
        CVEs: cves.size,
        Critical: rows.filter((r) => r.severity === 'Critical').length,
        High: rows.filter((r) => r.severity === 'High').length,
        Medium: rows.filter((r) => r.severity === 'Medium').length,
        Low: rows.filter((r) => r.severity === 'Low').length,
        Exploitable: rows.filter((r) => r.exploitLevel !== 'NoExploit').length,
        KEV: rows.filter((r) => r.isKEV).length,
        ...extraMetrics,
      },
      rows: asRecordRows(rows),
    });
  }, [asRecordRows, openDrilldown]);

  const openRecommendationDrilldown = useCallback((title: string, rows: TopRecommendation[], focus: string) => {
    openDrilldown({
      title,
      subtitle: 'Security recommendations returned by Defender. The dashboard currently keeps the top recommendations in-memory for this view.',
      kind: 'recommendation',
      focus,
      metrics: {
        Recommendations: rows.length,
        ExposedMachines: rows.reduce((acc, row) => acc + (row.exposedMachines ?? 0), 0),
        MaxSeverityScore: rows.reduce((acc, row) => Math.max(acc, row.severityScore ?? 0), 0),
        Active: rows.filter((row) => (row.status ?? '').toLowerCase() === 'active').length,
      },
      rows: asRecordRows(rows),
    });
  }, [asRecordRows, openDrilldown]);

  const openSoftwareDrilldown = useCallback((title: string, rows: TopSoftware[], focus: string) => {
    openDrilldown({
      title,
      subtitle: 'Software inventory risk details from Defender vulnerability management summary.',
      kind: 'software',
      focus,
      metrics: {
        SoftwareItems: rows.length,
        Weaknesses: rows.reduce((acc, row) => acc + (row.weaknesses ?? 0), 0),
        ExposedMachines: rows.reduce((acc, row) => acc + (row.exposedMachines ?? 0), 0),
        PublicExploit: rows.filter((row) => row.publicExploit).length,
      },
      rows: asRecordRows(rows),
    });
  }, [asRecordRows, openDrilldown]);

  const openThreatDrilldown = useCallback((title: string, rows: TopThreat[], focus: string) => {
    openDrilldown({
      title,
      subtitle: 'Threat intelligence CVE slice from Defender vulnerability management.',
      kind: 'threat',
      focus,
      metrics: {
        CVEs: rows.length,
        ExposedMachines: rows.reduce((acc, row) => acc + (row.exposedMachines ?? 0), 0),
        ExploitInWild: rows.filter((row) => row.exploitInWild).length,
        PublicExploit: rows.filter((row) => row.publicExploit).length,
        Critical: rows.filter((row) => row.severity === 'Critical').length,
      },
      rows: asRecordRows(rows),
    });
  }, [asRecordRows, openDrilldown]);

  const openDeviceDrilldown = useCallback((title: string, rows: DeviceStat[], focus: string) => {
    openDrilldown({
      title,
      subtitle: 'Device exposure aggregation derived from the current exposure records.',
      kind: 'device',
      focus,
      metrics: {
        Devices: rows.length,
        TotalUniqueCves: rows.reduce((acc, row) => acc + row.cveCount, 0),
        CriticalPairs: rows.reduce((acc, row) => acc + row.Critical, 0),
        HighPairs: rows.reduce((acc, row) => acc + row.High, 0),
      },
      rows: asRecordRows(rows),
    });
  }, [asRecordRows, openDrilldown]);

  const generateDrilldownAnalysis = useCallback(async () => {
    if (!drilldown || !selectedCustomerId || selectedCustomerId === 'default' || !canAnalyzeDrilldown) return;
    setDrilldownBusy(true);
    setDrilldownError(null);
    try {
      const data = await apiFetch<{ ok: boolean; markdown?: string; error?: string }>(
        `/vulnerability/card-analysis?customerId=${encodeURIComponent(selectedCustomerId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(drilldown),
        },
      );
      if (!data.ok || !data.markdown) throw new Error(data.error || 'AI drilldown analysis unavailable');
      setDrilldownAnalysis(data.markdown);
    } catch (e) {
      setDrilldownError((e as Error).message);
    } finally {
      setDrilldownBusy(false);
    }
  }, [selectedCustomerId, canAnalyzeDrilldown, drilldown]);

  const exportDrilldownCsv = useCallback(async () => {
    if (!drilldown || !selectedCustomerId || selectedCustomerId === 'default' || !canExportCardCsv) return;
    setDrilldownCsvBusy(true);
    setDrilldownError(null);
    try {
      const resp = await fetch(`${getApiBase()}/vulnerability/card-export-csv?customerId=${encodeURIComponent(selectedCustomerId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: drilldown.title,
          subtitle: drilldown.subtitle,
          kind: drilldown.kind,
          focus: drilldown.focus,
          metrics: drilldown.metrics,
          rows: drilldown.rows,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null) as { error?: string; message?: string } | null;
        throw new Error(body?.message || body?.error || `CSV export failed with HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const headerName = readContentDispositionFileName(resp.headers.get('Content-Disposition'));
      const fileName = headerName || `${safeDownloadFileName(drilldown.title)}-${new Date().toISOString().slice(0, 10)}.csv`;
      saveBlob(blob, fileName);
    } catch (e) {
      setDrilldownError((e as Error).message);
    } finally {
      setDrilldownCsvBusy(false);
    }
  }, [selectedCustomerId, canExportCardCsv, drilldown]);

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(col === 'update' || col === 'latestDate'); }
  };

  const allWarnings = [
    ...(summary?.warnings ?? []),
    ...(exposure?.warnings ?? []),
  ];

  return (
    <div className="space-y-4 slide-in">
      {/* Header banner: gradient navy to blue */}
      <div
        className="rounded-2xl p-6 text-white shadow-xl"
        style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #4c81d1 100%)' }}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[3px] opacity-80">Microsoft Defender for Endpoint</div>
            <h2 className="text-2xl font-light mt-1">Vulnerability Management Dashboard</h2>
            <p className="text-xs opacity-85 mt-1">
              Per device exposure across software, CVEs, and recommended remediations for the selected Defender tenant.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {exposure && (
              <div className="text-right text-[10px] opacity-80 mr-2">
                <div>Tenant: {selectedCustomerName}</div>
                <div>{exposure.recordCount.toLocaleString()} exposure record{exposure.recordCount === 1 ? '' : 's'}</div>
                <div>Generated {fmtTs(exposure.generatedAt)}</div>
                {exposure.cache && (
                  <div>Source: {exposure.cache.source === 'cache' ? 'DB cache' : 'Live API'} · expires {fmtTs(exposure.cache.expiresAt, { timeStyle: 'short' })}</div>
                )}
              </div>
            )}
            <button
              onClick={() => setWhatIfOpen(true)}
              disabled={!exposure || groups.length === 0 || !canUseWhatIf}
              title={canUseWhatIf ? 'Simulate a patch plan and project KPI impact' : 'Your role cannot use the What-If Simulator'}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur disabled:opacity-50 border border-white/20"
            >
              What-If Simulator
            </button>
            <button
              onClick={exportPdf}
              disabled={!exposure || pdfBusy || !canExportPdf}
              title={canExportPdf ? 'Generate an executive PDF brief' : 'Your role cannot export vulnerability PDFs'}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur disabled:opacity-50 border border-white/20"
            >
              {pdfBusy ? 'Exporting...' : 'Export PDF'}
            </button>
            <button
              onClick={() => void load(true)}
              disabled={loading || !canRefresh}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur disabled:opacity-50 border border-white/20"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm p-3 flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Vulnerability tenant</div>
          <div className="text-xs text-muted-foreground">Controls which customer tenant is queried for Defender VM data and cache.</div>
        </div>
        <select
          value={selectedCustomerId}
          onChange={(e) => handleSelectedCustomerChange(e.target.value)}
          className="min-w-[220px] rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {selectableCustomers.length === 0 ? (
            <option value="">No customer tenants available</option>
          ) : selectableCustomers.map((customer) => (
            <option key={customer.id} value={customer.id}>{customer.name}</option>
          ))}
        </select>
        {selectedCustomer && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            {selectedCustomer.logoUrl ? (
              <img src={selectedCustomer.logoUrl} alt={`${selectedCustomer.name} logo`} className="h-7 w-7 rounded-md object-contain bg-white border border-border p-0.5" />
            ) : selectedCustomer.color ? (
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: selectedCustomer.color }} />
            ) : null}
            <span>Selected: <strong className="text-foreground">{selectedCustomer.name}</strong></span>
            {summary?.defenderAuthMode && (
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px]">{defenderAuthModeLabel(summary.defenderAuthMode)}</span>
            )}
          </div>
        )}
        <button
          onClick={() => void load(true)}
          disabled={loading || !canRefresh || !selectedCustomerId}
          className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh selected tenant'}
        </button>
      </div>

      {selectedCustomerId === 'default' && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200">
          Select a customer tenant from the Vulnerability tenant selector to load Microsoft Defender Vulnerability Management data. The MSSP default workspace is intentionally blocked for this tenant-scoped module.
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-500/15 border border-red-500/40 text-red-700 dark:text-red-300 text-xs rounded-lg whitespace-pre-wrap">
          {error}
        </div>
      )}
      {allWarnings.length > 0 && (
        <div className="px-4 py-3 bg-amber-500/15 border border-amber-500/40 text-amber-800 dark:text-amber-200 text-xs rounded-lg">
          <div className="font-semibold mb-1">Partial data warning</div>
          <ul className="list-disc list-inside space-y-0.5">
            {allWarnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {loading && !exposure && (
        <div className="bg-card rounded-xl shadow-md p-8 border border-border text-center text-muted-foreground text-sm">
          Loading Defender Vulnerability Management data...
        </div>
      )}

      {exposure && (
        <>
          {/* Global active-filter banner. Filters persist across sub-tabs and
              affect PDF export, What-If, and the remediation table, so we
              surface them on every tab (not just where the controls live). */}
          {isFilterActive && (
            <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 flex items-center gap-3 flex-wrap text-xs">
              <span className="font-bold text-amber-700 dark:text-amber-300">Filters active</span>
              <span className="text-amber-900 dark:text-amber-100">
                Scope: <strong>{new Set(filtered.map((r) => r.deviceName).filter(Boolean)).size} device(s)</strong>,{' '}
                <strong>{new Set(filtered.map((r) => r.cveId).filter(Boolean)).size} unique CVE(s)</strong>.
                PDF export and What-If reflect this filtered scope.
              </span>
              <button onClick={() => { resetFilters(); setSubTab('devices'); }} className="text-[10px] underline text-amber-800 dark:text-amber-200">
                View / edit on Devices tab
              </button>
              <button onClick={resetFilters} className="ml-auto bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-0.5">
                Clear filters
              </button>
            </div>
          )}

          {/* Sub-tab navigation */}
          <VulnerabilitySubNav
            tabs={visibleSubTabs}
            active={subTab}
            layout={navLayout}
            collapsed={navCollapsed}
            onSelect={setSubTab}
            onSetLayout={setNavLayout}
            onToggleCollapsed={() => setNavCollapsed((v) => !v)}
          />

          {/* ========== EXECUTIVE ========== */}
          {subTab === 'executive' && (
            <>
              {patchTuesday && (
                <PatchTuesdayBanner
                  info={patchTuesday}
                  active={patchTuesdayOnly}
                  onToggle={() => setPatchTuesdayOnly((v) => !v)}
                />
              )}
              <AiBriefPanel
                markdown={aiBrief}
                loading={briefLoading}
                error={briefError}
                collapsed={briefCollapsed}
                onToggleCollapse={() => setBriefCollapsed((v) => !v)}
                onGenerate={generateBrief}
                disabled={!canGenerateBrief}
              />
              <div>
                <SecLabel>Executive Overview</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2.5">
                  <ScoreKpiCard value={summary?.executive?.exposureScore ?? null} label="Exposure Score" suffix="/100" hint="Lower is better" gradient="linear-gradient(135deg,#7c3aed 0%,#4c1d95 100%)" onClick={() => openExposureDrilldown('Exposure Score', 'Records that drive the tenant exposure score.', raw, 'Explain why the exposure score is high or low and which CVEs/software/device groups should be prioritized.')} />
                  <ScoreKpiCard value={summary?.executive?.secureScore ?? null} label="Secure Score" suffix="%" hint="Devices configuration" gradient="linear-gradient(135deg,#0ea5e9 0%,#0369a1 100%)" onClick={() => openRecommendationDrilldown('Secure Score Recommendations', summary?.patches?.topRecommendations ?? [], 'Explain which recommendations can improve Secure Score fastest and identify remediation order.')} />
                  <KpiCard num={summary?.executive?.criticalCves ?? kpi.critical} label="Critical CVEs" gradient="linear-gradient(135deg,#ff6b6b 0%,#c92a2a 100%)" onClick={() => openExposureDrilldown('Critical CVEs', 'All Critical exposure records in the current dataset.', raw.filter((r) => r.severity === 'Critical'), 'Prioritize Critical CVEs by exploitability, device count, KEV status and software owner.')} />
                  <KpiCard num={summary?.executive?.internetFacingDevices ?? 0} label="Internet Facing" gradient="linear-gradient(135deg,#f59e0b 0%,#b45309 100%)" onClick={() => openDeviceDrilldown('Internet Facing / High Exposure Devices', topDevices, 'Use the top exposed devices as a proxy list. Explain why internet-facing or high-exposure devices should be patched first.')} />
                  <KpiCard num={summary?.executive?.activeExploits ?? 0} label="Active Exploits" gradient="linear-gradient(135deg,#f87171 0%,#7f1d1d 100%)" onClick={() => openExposureDrilldown('Active Exploits', 'Exposure records with exploit kit, verified exploit, public exploit or KEV indicators.', raw.filter((r) => r.exploitLevel !== 'NoExploit' || r.isKEV), 'Focus on exploitability and give an emergency remediation order.')} />
                  <KpiCard num={summary?.executive?.slaBreaches ?? 0} label="SLA Breaches" gradient="linear-gradient(135deg,#fb7185 0%,#9f1239 100%)" onClick={() => openRecommendationDrilldown('SLA Breach Recommendations', summary?.patches?.topRecommendations ?? [], 'Explain which active recommendations likely breach the 30 day critical/high SLA and how to recover.')} />
                  <KpiCard num={kpi.activeDevices} label="Active Devices" gradient="linear-gradient(135deg,#60a5fa 0%,#2563eb 100%)" onClick={() => openDeviceDrilldown('Active Devices', topDevices, 'Summarize the highest-risk devices and the device-level remediation focus.')} />
                  <KpiCard num={kpi.exploitable} label="Exploitable Pairs" gradient="linear-gradient(135deg,#a78bfa 0%,#5b21b6 100%)" onClick={() => openExposureDrilldown('Exploitable Device/CVE Pairs', 'Device and CVE pairs with exploitable indicators.', raw.filter((r) => r.exploitLevel !== 'NoExploit' || r.isKEV), 'Prioritize exploitable device/CVE pairs by Critical/High severity and device concentration.')} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 ml-1">
                  SLA breach proxy: Critical or High recommendations still active after 30 days from first detection. Internet Facing devices are tagged in Defender or flagged with exposure level High.
                </p>
              </div>

              {summary?.severity && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <InsightCard title="Severity Distribution" subtitle="Unique CVEs in Defender catalog">
                    <SeverityDonut sev={summary.severity} />
                  </InsightCard>
                  <InsightCard title="Top 10 Most Exposed Devices" subtitle="Unique CVEs per device">
                    <TopDevicesChart data={topDevices} />
                  </InsightCard>
                </div>
              )}
            </>
          )}

          {/* ========== SEVERITY ========== */}
          {subTab === 'severity' && (
            <>
              <div>
                <SecLabel>Vulnerability Severity Distribution</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <KpiCard num={summary?.severity?.Critical ?? kpi.critical} label="Critical" gradient="linear-gradient(135deg,#ff6b6b 0%,#c92a2a 100%)" onClick={() => openExposureDrilldown('Critical Vulnerabilities', 'Critical severity exposure records.', raw.filter((r) => r.severity === 'Critical'), 'Explain all Critical recommendations/CVEs and remediation order.')} />
                  <KpiCard num={summary?.severity?.High ?? kpi.high} label="High" gradient="linear-gradient(135deg,#ffa94d 0%,#fd7e14 100%)" onClick={() => openExposureDrilldown('High Vulnerabilities', 'High severity exposure records.', raw.filter((r) => r.severity === 'High'), 'Explain High severity exposure drivers and remediation sequence.')} />
                  <KpiCard num={summary?.severity?.Medium ?? kpi.medium} label="Medium" gradient="linear-gradient(135deg,#ffe066 0%,#fcc419 100%)" darkText onClick={() => openExposureDrilldown('Medium Vulnerabilities', 'Medium severity exposure records.', raw.filter((r) => r.severity === 'Medium'), 'Explain whether any Medium risks should be promoted because of device concentration or exploitability.')} />
                  <KpiCard num={summary?.severity?.Low ?? kpi.low} label="Low" gradient="linear-gradient(135deg,#8ce99a 0%,#51cf66 100%)" darkText onClick={() => openExposureDrilldown('Low Vulnerabilities', 'Low severity exposure records.', raw.filter((r) => r.severity === 'Low'), 'Analyze the Low severity backlog and identify safe batching opportunities, repeated vendors and quick wins.')} />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <InsightCard title="Severity Donut" subtitle="Catalog-level CVE counts from Defender">
                  <SeverityDonut sev={summary?.severity ?? { Critical: kpi.critical, High: kpi.high, Medium: kpi.medium, Low: kpi.low }} />
                </InsightCard>
                <InsightCard title="Exposure Pairs by Severity" subtitle="Device + CVE pairs from exposure feed">
                  <SeverityBars sev={{ Critical: kpi.critical, High: kpi.high, Medium: kpi.medium, Low: kpi.low }} />
                </InsightCard>
              </div>
              <NoDataNote>
                Historical 30/60/90 day severity trends require persisted snapshots, which the platform does not yet collect for vulnerability data. Enable a daily snapshotter to unlock this view.
              </NoDataNote>
            </>
          )}

          {/* ========== DEVICES ========== */}
          {subTab === 'devices' && (
            <>
              <div>
                <SecLabel>Device Exposure</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <KpiCard num={summary?.devices?.total ?? kpi.activeDevices} label="Total Devices" gradient="linear-gradient(135deg,#60a5fa 0%,#2563eb 100%)" onClick={() => openDeviceDrilldown('Total Devices', topDevices, 'Summarize device exposure concentration and identify patch waves.')} />
                  <KpiCard num={summary?.devices?.vulnerable ?? 0} label="Vulnerable" gradient="linear-gradient(135deg,#f87171 0%,#991b1b 100%)" onClick={() => openDeviceDrilldown('Vulnerable Devices', topDevices, 'Rank vulnerable devices by CVE concentration and Critical/High exposure.')} />
                  <KpiCard num={summary?.devices?.unhealthy ?? 0} label="Unhealthy" gradient="linear-gradient(135deg,#fb923c 0%,#9a3412 100%)" onClick={() => openDeviceDrilldown('Unhealthy Devices', topDevices, 'Explain how unhealthy endpoints can affect remediation visibility and validation.')} />
                  <KpiCard num={summary?.devices?.missingPatches ?? 0} label="Missing Patches" gradient="linear-gradient(135deg,#a78bfa 0%,#5b21b6 100%)" onClick={() => openExposureDrilldown('Devices Missing Patches', 'Exposure records grouped by missing software/security updates.', raw.filter((r) => r.recommendedUpdate || r.fixingKbId), 'Identify recurring KBs/software updates and recommend patch waves by device impact.')} />
                </div>
              </div>
              {summary?.devices?.osDistribution && summary.devices.osDistribution.length > 0 && (
                <InsightCard title="Endpoint OS Distribution" subtitle="Active onboarded devices by platform">
                  <OsDistributionChart data={summary.devices.osDistribution} />
                </InsightCard>
              )}
              {isFilterActive && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 flex items-center gap-3 flex-wrap text-xs">
                  <span className="font-bold text-amber-700 dark:text-amber-300">Filter active</span>
                  <span className="text-amber-900 dark:text-amber-100">
                    <strong>{new Set(filtered.map((r) => r.deviceName).filter(Boolean)).size} device(s)</strong>,{' '}
                    <strong>{new Set(filtered.map((r) => r.cveId).filter(Boolean)).size} unique CVE(s)</strong>
                  </span>
                  <button onClick={resetFilters} className="ml-auto bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-0.5">
                    Clear filters
                  </button>
                </div>
              )}
              <FiltersBar
                search={search} setSearch={setSearch}
                sevF={sevF} setSevF={setSevF}
                expF={expF} setExpF={setExpF}
                osF={osF} setOsF={setOsF}
                grpF={grpF} setGrpF={setGrpF}
                osOptions={osOptions} grpOptions={grpOptions}
                onReset={resetFilters}
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <InsightCard title="Top 10 Most Exposed Devices" subtitle="Unique CVEs per device, responds to filters">
                  <TopDevicesChart data={topDevices} />
                </InsightCard>
                <InsightCard title="Missing Updates by Device" subtitle="Top 15 devices, stacked by severity">
                  <MissingUpdatesChart data={missingByDev} />
                </InsightCard>
              </div>
              <NoDataNote>
                Business Unit and Geography filters are not exposed by Defender for the active tenant. Filters above use the OS and RBAC group fields, which are the most granular dimensions available.
              </NoDataNote>
            </>
          )}

          {/* ========== PATCH COMPLIANCE ========== */}
          {subTab === 'patch' && (
            <>
              <div>
                <SecLabel>Patch Compliance</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <KpiCard num={summary?.patches?.missingKBs ?? 0} label="Missing KBs" gradient="linear-gradient(135deg,#fb923c 0%,#9a3412 100%)" onClick={() => openExposureDrilldown('Missing KBs', 'Exposure records with a KB or recommended update.', raw.filter((r) => r.fixingKbId || /KB\d+/i.test(r.recommendedUpdate ?? '')), 'Prioritize missing KBs by devices impacted, Critical/High CVEs and exploitability.')} />
                  <PctKpiCard value={summary?.patches?.deploymentSuccessPct ?? null} label="Deployment Success" gradient="linear-gradient(135deg,#34d399 0%,#047857 100%)" onClick={() => openRecommendationDrilldown('Deployment Success', summary?.patches?.topRecommendations ?? [], 'Explain deployment success and where active recommendations indicate remaining remediation debt.')} />
                  <DaysKpiCard value={summary?.patches?.avgRemediationDays ?? null} label="Avg Remediation Age" gradient="linear-gradient(135deg,#fbbf24 0%,#b45309 100%)" onClick={() => openRecommendationDrilldown('Average Remediation Age', summary?.patches?.topRecommendations ?? [], 'Analyze aging recommendations and call out remediation aging risk.')} />
                  <KpiCard num={summary?.patches?.unsupportedOs ?? 0} label="Unsupported OS" gradient="linear-gradient(135deg,#f87171 0%,#7f1d1d 100%)" onClick={() => openSoftwareDrilldown('Unsupported OS / EOL Software', (summary?.software?.topSoftware ?? []).filter((s) => s.endOfSupportStatus === 'EOS'), 'Explain unsupported OS/software risk and migration/compensating control actions.')} />
                </div>
              </div>
              {patchTuesday && (
                <PatchTuesdayBanner
                  info={patchTuesday}
                  active={patchTuesdayOnly}
                  onToggle={() => setPatchTuesdayOnly((v) => !v)}
                />
              )}
              {summary?.patches?.topRecommendations && summary.patches.topRecommendations.length > 0 && (
                <InsightCard title="Top Active Recommendations" subtitle="From Defender Security Recommendations API, sorted by exposure impact">
                  <TopRecommendationsTable rows={summary.patches.topRecommendations} />
                </InsightCard>
              )}
              <FiltersBar
                search={search} setSearch={setSearch}
                sevF={sevF} setSevF={setSevF}
                expF={expF} setExpF={setExpF}
                osF={osF} setOsF={setOsF}
                grpF={grpF} setGrpF={setGrpF}
                osOptions={osOptions} grpOptions={grpOptions}
                onReset={resetFilters}
              />
              <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
                <div className="px-4 py-2.5 border-b border-border flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs font-semibold text-foreground">Remediation Groups</div>
                  <div className="text-[11px] text-muted-foreground">
                    {groups.length} group{groups.length === 1 ? '' : 's'}
                    {' / '}
                    {filtered.length.toLocaleString()} record{filtered.length === 1 ? '' : 's'}
                    {' / '}
                    {new Set(filtered.map((r) => r.cveId).filter(Boolean)).size} unique CVE{new Set(filtered.map((r) => r.cveId).filter(Boolean)).size === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <SortHeader col="update" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort} align="left">Remediation</SortHeader>
                        <SortHeader col="deviceCount" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort}>Assets</SortHeader>
                        <SortHeader col="cveCount" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort}>CVEs</SortHeader>
                        <SortHeader col="exploits" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort}>Exploits</SortHeader>
                        <SortHeader col="kits" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort}>In Kit</SortHeader>
                        <SortHeader col="topSev" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort} align="left">Top Severity</SortHeader>
                        <SortHeader col="avgCvss" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort}>Avg CVSS</SortHeader>
                        <SortHeader col="latestDate" sortCol={sortCol} sortAsc={sortAsc} onClick={toggleSort} align="left">Published</SortHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.length === 0 && (
                        <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">
                          No records match the current filters.
                        </td></tr>
                      )}
                      {groups.map((g) => {
                        const maxAssets = Math.max(1, ...groups.map((x) => x.deviceCount));
                        const cvssColor = g.avgCvss >= 9 ? '#c92a2a' : g.avgCvss >= 7 ? '#d9480f' : g.avgCvss >= 4 ? '#e67700' : '#2b8a3e';
                        const updateText = g.update || g.software || '-';
                        const kbMatch = updateText.match(/KB\d+/i);
                        return (
                          <tr
                            key={g.key}
                            onClick={() => setOpenGroup(g)}
                            className="border-t border-border/50 hover:bg-muted/30 cursor-pointer"
                          >
                            <td className="px-3 py-2">
                              {kbMatch ? (
                                <a
                                  href={`https://www.catalog.update.microsoft.com/Search.aspx?q=${kbMatch[0]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {updateText}
                                </a>
                              ) : (
                                <div className="font-semibold text-foreground">{updateText}</div>
                              )}
                              <div className="text-[10px] text-muted-foreground">{g.vendor || ''}</div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="font-bold text-foreground">{g.deviceCount}</div>
                              <div className="mt-0.5 h-1 bg-muted/40 rounded overflow-hidden">
                                <div className="h-full bg-blue-500" style={{ width: `${(g.deviceCount / maxAssets) * 100}%` }} />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center font-mono">{g.cveCount}</td>
                            <td className="px-3 py-2 text-center font-mono" style={{ color: g.exploits > 0 ? '#d9480f' : undefined }}>{g.exploits}</td>
                            <td className="px-3 py-2 text-center font-mono" style={{ color: g.kits > 0 ? '#c92a2a' : undefined }}>{g.kits}</td>
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] border ${SEV_PILL[g.topSev]}`}>{g.topSev}</span>
                            </td>
                            <td className="px-3 py-2 text-center font-mono font-semibold" style={{ color: cvssColor }}>{g.avgCvss.toFixed(1)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{g.latestDate ? g.latestDate.slice(0, 10) : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <NoDataNote>
                SLA breach matrix and monthly remediation trend rely on persisted snapshots that are not yet collected for vulnerability data. The active SLA breach count is surfaced on the Executive tab.
              </NoDataNote>
            </>
          )}

          {/* ========== SOFTWARE RISK ========== */}
          {subTab === 'software' && (
            <>
              <div>
                <SecLabel>Software Inventory Risk</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <KpiCard num={summary?.software?.eolCount ?? 0} label="End of Life" gradient="linear-gradient(135deg,#f87171 0%,#7f1d1d 100%)" onClick={() => openSoftwareDrilldown('End-of-Life Software', (summary?.software?.topSoftware ?? []).filter((s) => s.endOfSupportStatus === 'EOS'), 'Prioritize EOL software by exposed machines, exploitability and compensating control needs.')} />
                  <KpiCard num={summary?.software?.vulnerableApps ?? 0} label="Vulnerable Apps" gradient="linear-gradient(135deg,#fb923c 0%,#9a3412 100%)" onClick={() => openSoftwareDrilldown('Vulnerable Applications', summary?.software?.topSoftware ?? [], 'Rank vulnerable applications by weaknesses, exposed machines and public exploit status.')} />
                  <KpiCard num={summary?.software?.browserVulns ?? 0} label="Browser Weaknesses" gradient="linear-gradient(135deg,#60a5fa 0%,#1e3a8a 100%)" onClick={() => openSoftwareDrilldown('Browser Weaknesses', (summary?.software?.topSoftware ?? []).filter((s) => /chrome|firefox|edge|safari|brave|opera/i.test(s.name)), 'Recommend browser patch cadence and urgent browser remediation scope.')} />
                  <KpiCard num={summary?.software?.thirdPartyExposure ?? 0} label="Third Party Exposure" gradient="linear-gradient(135deg,#a78bfa 0%,#5b21b6 100%)" onClick={() => openSoftwareDrilldown('Third Party Software Exposure', (summary?.software?.topSoftware ?? []).filter((s) => !/microsoft|windows/i.test(s.vendor)), 'Prioritize third-party software updates and owner assignment.')} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 ml-1">
                  Browser weaknesses sum CVEs across Chrome, Firefox, Edge, Safari, Brave and Opera. Third party exposure totals exposed machines across software whose vendor is not Microsoft.
                </p>
              </div>
              {summary?.software?.topSoftware && summary.software.topSoftware.length > 0 && (
                <InsightCard title="Top Vulnerable Software" subtitle="From Defender Software Inventory, sorted by exposed machines">
                  <TopSoftwareTable rows={summary.software.topSoftware} />
                </InsightCard>
              )}
            </>
          )}

          {/* ========== ZERO-DAY & TI ========== */}
          {subTab === 'zeroday' && (
            <>
              <div>
                <SecLabel>Zero-Day &amp; Threat Intelligence</SecLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <KpiCard num={summary?.threatIntel?.activeExploitCves ?? 0} label="Active Exploits" gradient="linear-gradient(135deg,#f87171 0%,#7f1d1d 100%)" onClick={() => openThreatDrilldown('Active Exploit CVEs', (summary?.threatIntel?.topThreats ?? []).filter((t) => t.exploitInWild), 'Prioritize active exploit CVEs by exposed machines, CVSS and patch urgency.')} />
                  <KpiCard num={summary?.threatIntel?.publicExploitCves ?? 0} label="Public Exploits" gradient="linear-gradient(135deg,#fb923c 0%,#9a3412 100%)" onClick={() => openThreatDrilldown('Public Exploit CVEs', (summary?.threatIntel?.topThreats ?? []).filter((t) => t.publicExploit), 'Explain which public exploit CVEs should be patched immediately and why.')} />
                  <KpiCard num={summary?.threatIntel?.ransomwareLinkedCves ?? 0} label="Ransomware Linked CVEs" gradient="linear-gradient(135deg,#dc2626 0%,#450a0a 100%)" onClick={() => openThreatDrilldown('Ransomware Linked CVEs', summary?.threatIntel?.topThreats ?? [], 'Find ransomware-relevant CVEs or products from the provided threat set and recommend containment/patch actions.')} />
                  <KpiCard num={summary?.threatIntel?.ransomwareLinkedProducts ?? 0} label="Ransomware Linked Products" gradient="linear-gradient(135deg,#a78bfa 0%,#5b21b6 100%)" onClick={() => openThreatDrilldown('Ransomware Linked Products', summary?.threatIntel?.topThreats ?? [], 'Group ransomware-linked exposure by affected product/software where possible.')} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 ml-1">
                  Sources: Microsoft Defender Threat Intel feed (active and public exploit flags on each CVE), and CISA KEV via Defender exploit type strings. Ransomware linkage joins recommendation associatedThreats to product names.
                </p>
              </div>
              {summary?.threatIntel?.topThreats && summary.threatIntel.topThreats.length > 0 && (
                <InsightCard
                  title="Top Threats"
                  subtitle="In-the-wild or public exploit, or Critical severity"
                >
                  <TopThreatsTable
                    rows={summary.threatIntel.topThreats}
                    attackMap={attackMap}
                    canMapAttack={canMapAttack}
                    onMapAll={() => fetchAttackMap(
                      (summary.threatIntel?.topThreats ?? []).map((t) => ({
                        cveId: t.cveId,
                        description: t.description,
                        software: t.name,
                      })),
                    )}
                  />
                </InsightCard>
              )}
            </>
          )}

          {summary?.executive && (summary.executive.exposureScore !== null || summary.executive.secureScore !== null) && (
            <div className="text-[10px] text-muted-foreground text-right pr-2">
              {summary.executive.exposureScore !== null && <>Exposure Score: <strong>{summary.executive.exposureScore.toFixed(1)}</strong> / 100 (lower is better) &nbsp;|&nbsp; </>}
              {summary.executive.secureScore !== null && <>Secure Score for Devices: <strong>{summary.executive.secureScore.toFixed(1)}%</strong></>}
            </div>
          )}
        </>
      )}

      {drilldown && (
        <VulnerabilityDrilldownModal
          payload={drilldown}
          markdown={drilldownAnalysis}
          loading={drilldownBusy}
          error={drilldownError}
          canAnalyze={canAnalyzeDrilldown}
          canExportCsv={canExportCardCsv}
          csvLoading={drilldownCsvBusy}
          onAnalyze={generateDrilldownAnalysis}
          onExportCsv={exportDrilldownCsv}
          onClose={() => setDrilldown(null)}
        />
      )}

      {openGroup && (
        <RemediationModal
          group={openGroup}
          onClose={() => setOpenGroup(null)}
          attackMap={attackMap}
          onFetchAttack={fetchAttackMap}
          canMapAttack={canMapAttack}
        />
      )}
      {whatIfOpen && canUseWhatIf && (
        <WhatIfModal
          groups={groups}
          kpi={kpi}
          plan={whatIfPlan}
          onTogglePlan={(key) => setWhatIfPlan((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
          })}
          onClear={() => setWhatIfPlan(new Set())}
          onClose={() => setWhatIfOpen(false)}
          computePlan={computeWhatIfPlan}
        />
      )}
    </div>
  );
}

function VulnerabilitySubNav({
  tabs, active, layout, collapsed, onSelect, onSetLayout, onToggleCollapsed,
}: {
  tabs: { id: SubTab; label: string }[];
  active: SubTab;
  layout: VmSubNavLayout;
  collapsed: boolean;
  onSelect: (tab: SubTab) => void;
  onSetLayout: (layout: VmSubNavLayout) => void;
  onToggleCollapsed: () => void;
}) {
  const activeTab = tabs.find((tab) => tab.id === active) ?? tabs[0];
  return (
    <div className="rounded-xl border border-border bg-card/70 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/70">
        <div className="text-[11px] font-bold uppercase tracking-[1.8px] text-muted-foreground">Vulnerability Views</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSetLayout('horizontal')}
            className={`text-[10px] px-2 py-1 rounded border ${layout === 'horizontal' ? 'bg-blue-600 text-white border-blue-600' : 'border-border bg-background hover:bg-muted/40'}`}
          >
            Horizontal
          </button>
          <button
            type="button"
            onClick={() => onSetLayout('hamburger')}
            className={`text-[10px] px-2 py-1 rounded border ${layout === 'hamburger' ? 'bg-blue-600 text-white border-blue-600' : 'border-border bg-background hover:bg-muted/40'}`}
          >
            Hamburger
          </button>
        </div>
      </div>

      {layout === 'horizontal' ? (
        <div className="flex gap-1 overflow-x-auto px-2">
          {tabs.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t.id)}
                className={`px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="w-full px-3 py-2 flex items-center justify-between gap-3 text-left hover:bg-muted/30 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="text-lg leading-none">☰</span>
              <span>{activeTab ? activeTab.label : 'Select view'}</span>
            </span>
            <span className="text-xs text-muted-foreground">{collapsed ? 'Expand' : 'Collapse'} ▾</span>
          </button>
          {!collapsed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3 border-t border-border/70 bg-muted/10">
              {tabs.map((t) => {
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { onSelect(t.id); onToggleCollapsed(); }}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                      isActive
                        ? 'border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300'
                        : 'border-border bg-card hover:bg-muted/40 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VulnerabilityDrilldownModal({
  payload, markdown, loading, csvLoading, error, canAnalyze, canExportCsv, onAnalyze, onExportCsv, onClose,
}: {
  payload: DrilldownPayload;
  markdown: string | null;
  loading: boolean;
  csvLoading: boolean;
  error: string | null;
  canAnalyze: boolean;
  canExportCsv: boolean;
  onAnalyze: () => void;
  onExportCsv: () => void;
  onClose: () => void;
}) {
  const rows = payload.rows.slice(0, 200);
  const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
    .filter((key) => !['id', 'deviceId', 'description'].includes(key))
    .slice(0, 8);
  const detailColumns: FilterableColumnDef<Record<string, unknown>>[] = keys.map((key) => ({
    key,
    header: key,
    value: (row) => row[key] ?? '',
    cellClassName: 'max-w-[260px] truncate',
    render: (row) => <span title={String(row[key] ?? '')}>{String(row[key] ?? '-')}</span>,
  }));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-card text-card-foreground rounded-2xl shadow-2xl border border-border max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[2px] text-muted-foreground">Vulnerability card drilldown</div>
            <h3 className="text-lg font-bold text-foreground mt-1">{payload.title}</h3>
            {payload.subtitle && <p className="text-xs text-muted-foreground mt-1">{payload.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onAnalyze}
              disabled={loading || !canAnalyze}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              title={canAnalyze ? 'Generate AI analysis for this exact card' : 'Your role cannot run card AI analysis'}
            >
              {loading ? 'Analyzing...' : 'AI Analysis'}
            </button>
            <button
              type="button"
              onClick={onExportCsv}
              disabled={csvLoading || !canExportCsv}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              title={canExportCsv ? 'Export all rows available for this card to CSV. This action is audited.' : 'Your role cannot export vulnerability card CSV files'}
            >
              {csvLoading ? 'Exporting...' : 'Export CSV'}
            </button>
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted/30">Close</button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {Object.entries(payload.metrics).map(([key, value]) => (
              <div key={key} className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{key}</div>
                <div className="text-lg font-bold text-foreground mt-0.5">{typeof value === 'number' ? value.toLocaleString() : value}</div>
              </div>
            ))}
          </div>

          {payload.focus && (
            <div className="rounded-lg border border-blue-400/40 bg-blue-500/10 px-3 py-2 text-xs text-blue-900 dark:text-blue-100">
              <span className="font-semibold">AI prompt focus:</span> {payload.focus}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</div>
          )}

          {markdown && (
            <div className="rounded-xl border border-purple-400/40 bg-purple-500/5 p-4 text-xs prose prose-sm dark:prose-invert max-w-none">
              <MarkdownBrief md={markdown} />
            </div>
          )}

          <div className="rounded-xl border border-border overflow-hidden">
            <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between">
              <div className="text-xs font-semibold">Detailed records</div>
              <div className="text-[10px] text-muted-foreground">Showing {rows.length.toLocaleString()} of {payload.rows.length.toLocaleString()} sampled rows</div>
            </div>
            {detailColumns.length === 0 ? (
              <div className="px-3 py-8 text-center text-muted-foreground text-xs">No detailed records are available for this card.</div>
            ) : (
              <FilterableColumnTable
                columns={detailColumns}
                rows={rows}
                storageKey={`kloudsecure:vulnerability:drilldown:${payload.kind}:${payload.title}:columns`}
                rowKey={(_, idx) => idx}
                rowClassName={() => 'border-t border-border/60 hover:bg-muted/20'}
                emptyMessage="No detailed records match the current column filters."
                maxHeightClassName="max-h-[360px]"
                tableClassName="w-full text-xs"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SecLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] font-bold uppercase tracking-[2px] text-muted-foreground mb-2 mt-1">
      {children}
    </div>
  );
}

function KpiCard({ num, label, gradient, darkText, onClick }: { num: number; label: string; gradient: string; darkText?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-md p-3 shadow text-center overflow-hidden w-full transition-transform ${onClick ? 'cursor-pointer hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-blue-400/60' : 'cursor-default'}`}
      style={{ background: gradient, color: darkText ? '#333' : '#fff' }}
      title={onClick ? `Open ${label} details` : undefined}
    >
      <div className="text-2xl font-extrabold leading-none">{num.toLocaleString()}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1.5 opacity-85">{label}</div>
    </button>
  );
}

function InsightCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="bg-card rounded-xl shadow-md border border-border p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-bold text-foreground">{title}</h3>
        {subtitle && <span className="text-[10px] text-muted-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function TopDevicesChart({ data }: { data: DeviceStat[] }) {
  if (data.length === 0) return <div className="text-xs text-muted-foreground py-6 text-center">No devices in scope.</div>;
  const max = Math.max(1, ...data.map((d) => d.cveCount));
  return (
    <div className="space-y-1.5">
      {data.map((d) => (
        <div key={d.name} className="flex items-center gap-2 text-[11px]">
          <div className="w-40 truncate text-muted-foreground" title={d.name}>{d.name}</div>
          <div className="flex-1 bg-muted/30 rounded h-4 overflow-hidden border border-border">
            <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600" style={{ width: `${(d.cveCount / max) * 100}%` }} />
          </div>
          <div className="w-10 text-right font-mono font-semibold text-foreground">{d.cveCount}</div>
        </div>
      ))}
    </div>
  );
}

function MissingUpdatesChart({ data }: { data: DeviceStat[] }) {
  if (data.length === 0) return <div className="text-xs text-muted-foreground py-6 text-center">No devices in scope.</div>;
  const max = Math.max(1, ...data.map((d) => d.total));
  const order: (keyof typeof SEV_BAR)[] = ['Critical', 'High', 'Medium', 'Low'];
  return (
    <div className="space-y-1">
      {data.map((d) => (
        <div key={d.name} className="flex items-center gap-2 text-[11px]">
          <div className="w-40 truncate text-muted-foreground" title={d.name}>{d.name}</div>
          <div className="flex-1 flex h-4 bg-muted/30 rounded overflow-hidden border border-border">
            {order.map((sev) => {
              const v = d[sev];
              if (v === 0) return null;
              const pct = (v / max) * 100;
              return <div key={sev} className={SEV_BAR[sev]} style={{ width: `${pct}%` }} title={`${sev}: ${v}`} />;
            })}
          </div>
          <div className="w-10 text-right font-mono font-semibold text-foreground">{d.total}</div>
        </div>
      ))}
      <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
        {order.map((s) => (
          <div key={s} className="flex items-center gap-1">
            <span className={`w-2.5 h-2.5 rounded-sm ${SEV_BAR[s]}`} /> {s}
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}

function SortHeader({
  col, sortCol, sortAsc, onClick, children, align,
}: {
  col: SortCol; sortCol: SortCol | null; sortAsc: boolean;
  onClick: (col: SortCol) => void; children: ReactNode; align?: 'left' | 'center';
}) {
  const isActive = sortCol === col;
  return (
    <th
      className={`px-3 py-2 cursor-pointer select-none hover:bg-muted/60 ${align === 'left' ? 'text-left' : 'text-center'}`}
      onClick={() => onClick(col)}
    >
      {children}
      <span className="ml-1 text-[9px]">{isActive ? (sortAsc ? '\u25B2' : '\u25BC') : '\u2195'}</span>
    </th>
  );
}

function RemediationModal({
  group, onClose, attackMap, onFetchAttack, canMapAttack,
}: {
  group: RemediationGroup;
  onClose: () => void;
  attackMap: AttackMap;
  onFetchAttack: (cves: { cveId: string; description?: string; software?: string }[]) => Promise<void>;
  canMapAttack: boolean;
}) {
  const devs = Array.from(new Set(group.rows.map((r) => r.deviceName).filter(Boolean))).sort();
  const cveMap = new Map<string, ExposureRow>();
  for (const r of group.rows) {
    if (r.cveId && !cveMap.has(r.cveId)) cveMap.set(r.cveId, r);
  }
  const cves = Array.from(cveMap.values()).sort((a, b) => SEV_ORD[a.severity] - SEV_ORD[b.severity]);
  const [attackBusy, setAttackBusy] = useState(false);
  const uncovered = cves.filter((c) => !attackMap[c.cveId.toUpperCase()]).length;
  const handleMapAttack = async () => {
    if (!canMapAttack) return;
    setAttackBusy(true);
    try {
      await onFetchAttack(cves.map((c) => ({
        cveId: c.cveId,
        description: c.description,
        software: `${c.softwareVendor ?? ''} ${c.softwareName ?? ''}`.trim() || undefined,
      })));
    } finally {
      setAttackBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground truncate">
              {group.update && group.update !== '-' ? group.update : group.software || 'Details'}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {group.vendor}{group.software && group.software !== '-' ? ` / ${group.software}` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none px-2"
            aria-label="Close"
          >
            {'\u2715'}
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ModalStat num={group.deviceCount} label="Devices" color="#4c81d1" />
            <ModalStat num={group.cveCount} label="Unique CVEs" color="#764ba2" />
            <ModalStat num={group.exploits} label="Exploits" color="#d9480f" />
            <ModalStat num={group.kits} label="In Kit" color="#c92a2a" />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Affected Devices ({devs.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {devs.map((d) => (
                <span key={d} className="px-2 py-1 text-[11px] rounded bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/30 font-mono">
                  {d}
                </span>
              ))}
              {devs.length === 0 && <span className="text-xs text-muted-foreground">No device names in scope.</span>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                CVE Details ({cves.length} unique)
              </div>
              <button
                onClick={handleMapAttack}
                disabled={attackBusy || uncovered === 0 || !canMapAttack}
                title="Map each CVE to MITRE ATT&CK techniques an attacker would use post-exploit"
                className="text-[10px] font-semibold px-2.5 py-1 rounded border border-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 disabled:opacity-50"
              >
                {attackBusy ? 'Mapping...' : uncovered === 0 ? 'ATT&CK mapped' : `Map ATT&CK (${uncovered})`}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">CVE</th>
                    <th className="px-2 py-1.5 text-left">Severity</th>
                    <th className="px-2 py-1.5 text-right">CVSS</th>
                    <th className="px-2 py-1.5 text-left">Exploit</th>
                    <th className="px-2 py-1.5 text-left">Intelligence</th>
                    <th className="px-2 py-1.5 text-left">ATT&CK</th>
                    <th className="px-2 py-1.5 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {cves.map((c) => (
                    <tr key={c.cveId} className="border-t border-border/50">
                      <td className="px-2 py-1.5 font-mono">
                        <a
                          href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(c.cveId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {c.cveId}
                        </a>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] border ${SEV_PILL[c.severity]}`}>{c.severity}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{c.cvssScore.toFixed(1)}</td>
                      <td className="px-2 py-1.5">{EXP_LBL[c.exploitLevel]}</td>
                      <td className="px-2 py-1.5">
                        <IntelBadges row={c} />
                      </td>
                      <td className="px-2 py-1.5">
                        <AttackChips techniques={attackMap[c.cveId.toUpperCase()]} />
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground max-w-md">
                        <div className="line-clamp-3" title={c.description}>{c.description || '-'}</div>
                      </td>
                    </tr>
                  ))}
                  {cves.length === 0 && (
                    <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">No CVE detail available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalStat({ num, label, color }: { num: number; label: string; color: string }) {
  return (
    <div className="bg-muted/20 border border-border rounded-lg p-3 text-center">
      <div className="text-2xl font-extrabold" style={{ color }}>{num}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function IntelBadges({ row }: { row: ExposureRow }) {
  const isVendorMs = row.softwareVendor.toLowerCase().includes('microsoft');
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {row.isKEV && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-600 text-white font-bold">KEV</span>
      )}
      {row.exploitLevel === 'ExploitIsInKit' && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-200">Exploit Kit</span>
      )}
      {row.exploitLevel === 'ExploitIsVerified' && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-200 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200">Verified</span>
      )}
      {row.exploitLevel === 'ExploitIsPublic' && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">Public Exploit</span>
      )}
      {row.pocUrl && (
        <a href={row.pocUrl} target="_blank" rel="noopener noreferrer" className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-700 dark:text-blue-300 hover:underline">PoC</a>
      )}
      {row.cveId && isVendorMs && (
        <a
          href={`https://msrc.microsoft.com/update-guide/vulnerability/${row.cveId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-700 dark:text-blue-300 hover:underline"
        >
          MSRC
        </a>
      )}
    </div>
  );
}

function AttackChips({ techniques }: { techniques: AttackTech[] | undefined }) {
  if (!techniques) return <span className="text-[10px] text-muted-foreground">-</span>;
  if (techniques.length === 0) return <span className="text-[10px] text-muted-foreground italic">No mapping</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {techniques.map((t) => (
        <a
          key={t.id}
          href={`https://attack.mitre.org/techniques/${t.id.replace('.', '/')}/`}
          target="_blank"
          rel="noopener noreferrer"
          title={`${t.tactic}: ${t.name}`}
          className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/30 hover:bg-purple-500/25"
        >
          {t.id}
        </a>
      ))}
    </div>
  );
}

function PatchTuesdayBanner({
  info, active, onToggle,
}: { info: PatchTuesdayInfo; active: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-xl border border-blue-400/50 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 px-4 py-3 flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div>
          <div className="text-xs font-bold text-blue-800 dark:text-blue-200">
            Microsoft Patch Tuesday: {info.label}
          </div>
          <div className="text-[11px] text-blue-900/70 dark:text-blue-100/80">
            <strong>{info.uniqueCves}</strong> unique CVE(s) from this release affect{' '}
            <strong>{info.devices}</strong> device(s) across <strong>{info.records.length}</strong> exposure record(s).
          </div>
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`ml-auto text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
          active
            ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
            : 'bg-white/40 dark:bg-white/5 border-blue-400/40 text-blue-800 dark:text-blue-200 hover:bg-blue-500/15'
        }`}
      >
        {active ? 'Showing Patch Tuesday only' : 'Filter to Patch Tuesday'}
      </button>
    </div>
  );
}

function AiBriefPanel({
  markdown, loading, error, collapsed, onToggleCollapse, onGenerate, disabled = false,
}: {
  markdown: string | null; loading: boolean; error: string | null;
  collapsed: boolean; onToggleCollapse: () => void; onGenerate: () => void; disabled?: boolean;
}) {
  return (
    <div className="rounded-xl border border-purple-400/40 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-bold text-sm text-purple-900 dark:text-purple-200">AI Exposure Brief</div>
        <div className="text-[11px] text-muted-foreground">
          Executive summary of your current vulnerability posture, written by Azure OpenAI.
        </div>
        <div className="ml-auto flex items-center gap-2">
          {markdown && (
            <button
              onClick={onToggleCollapse}
              className="text-[11px] font-semibold px-2.5 py-1 rounded border border-border bg-card hover:bg-muted/30"
            >
              {collapsed ? 'Show' : 'Hide'}
            </button>
          )}
          <button
            onClick={onGenerate}
            disabled={loading || disabled}
            className="text-[11px] font-semibold px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
          >
            {loading ? 'Generating...' : markdown ? 'Regenerate' : 'Generate brief'}
          </button>
        </div>
      </div>
      {error && (
        <div className="mt-2 text-[11px] text-red-700 dark:text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      {markdown && !collapsed && (
        <div className="mt-3 bg-card/60 border border-border rounded-lg p-3 text-xs prose prose-sm dark:prose-invert max-w-none">
          <MarkdownBrief md={markdown} />
        </div>
      )}
      {!markdown && !loading && !error && (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
          {disabled ? 'Your role is not allowed to generate AI vulnerability briefs.' : 'Click Generate brief to produce a CISO-grade narrative with prioritized patches and business impact.'}
        </div>
      )}
    </div>
  );
}

function MarkdownBrief({ md }: { md: string }) {
  const lines = md.split(/\r?\n/);
  const nodes: ReactNode[] = [];
  let listBuf: string[] = [];
  let listKind: 'ul' | 'ol' | null = null;
  const flushList = () => {
    if (listBuf.length === 0) return;
    const items = listBuf.map((t, i) => <li key={i}>{t}</li>);
    nodes.push(listKind === 'ol'
      ? <ol key={`l${nodes.length}`} className="list-decimal list-inside space-y-0.5">{items}</ol>
      : <ul key={`l${nodes.length}`} className="list-disc list-inside space-y-0.5">{items}</ul>);
    listBuf = [];
    listKind = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith('## ')) {
      flushList();
      nodes.push(<h4 key={`h${i}`} className="font-bold mt-2 mb-1 text-purple-800 dark:text-purple-200">{line.slice(3)}</h4>);
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      nodes.push(<h3 key={`h${i}`} className="font-bold text-sm mt-2 mb-1">{line.slice(2)}</h3>);
      continue;
    }
    const ol = line.match(/^\d+\.\s+(.*)$/);
    const ul = line.match(/^[-*]\s+(.*)$/);
    if (ol) {
      if (listKind && listKind !== 'ol') flushList();
      listKind = 'ol'; listBuf.push(ol[1]); continue;
    }
    if (ul) {
      if (listKind && listKind !== 'ul') flushList();
      listKind = 'ul'; listBuf.push(ul[1]); continue;
    }
    flushList();
    nodes.push(<p key={`p${i}`} className="leading-relaxed">{line}</p>);
  }
  flushList();
  return <>{nodes}</>;
}

function WhatIfModal({
  groups, kpi, plan, onTogglePlan, onClear, onClose, computePlan,
}: {
  groups: RemediationGroup[];
  kpi: { activeDevices: number; totalRecords: number; critical: number; high: number; medium: number; low: number; exploitable: number };
  plan: Set<string>;
  onTogglePlan: (key: string) => void;
  onClear: () => void;
  onClose: () => void;
  computePlan: () => { items: { update: string; devices: number; cves: number }[]; devicesRemoved: number; cvesRemoved: number; criticalRemoved: number } | null;
}) {
  const top = useMemo(() => [...groups].sort((a, b) => b.deviceCount - a.deviceCount).slice(0, 30), [groups]);
  const computed = computePlan();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-foreground">What-If Patch Simulator</div>
            <div className="text-[11px] text-muted-foreground">
              Select remediations to deploy. KPI impact is projected live based on de-duped device + CVE coverage.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none px-2"
            aria-label="Close"
          >
            {'\u2715'}
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ModalStat num={computed?.devicesRemoved ?? 0} label="Devices Cleared" color="#16a34a" />
            <ModalStat num={computed?.cvesRemoved ?? 0} label="CVEs Cleared" color="#2563eb" />
            <ModalStat num={computed?.criticalRemoved ?? 0} label="Critical Pairs Cleared" color="#dc2626" />
            <ModalStat num={plan.size} label="Patches Selected" color="#7c3aed" />
          </div>

          <div className="rounded-lg border border-green-500/30 bg-green-500/5 px-3 py-2 text-[11px] text-green-900 dark:text-green-200">
            Baseline: <strong>{kpi.activeDevices}</strong> active devices, <strong>{kpi.totalRecords}</strong> total exposure records,{' '}
            <strong>{kpi.critical}</strong> Critical pairs. Projected after patching: tap rows below to add.
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Top remediations by device coverage (showing {top.length} of {groups.length})
              </div>
              <button
                onClick={onClear}
                disabled={plan.size === 0}
                className="text-[10px] font-semibold px-2 py-0.5 rounded border border-border hover:bg-muted/30 disabled:opacity-50"
              >
                Clear plan
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-8"></th>
                    <th className="px-2 py-1.5 text-left">Remediation</th>
                    <th className="px-2 py-1.5 text-left">Vendor</th>
                    <th className="px-2 py-1.5 text-right">Devices</th>
                    <th className="px-2 py-1.5 text-right">CVEs</th>
                    <th className="px-2 py-1.5 text-left">Top Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((g) => {
                    const checked = plan.has(g.key);
                    return (
                      <tr
                        key={g.key}
                        className={`border-t border-border/50 cursor-pointer hover:bg-muted/30 ${checked ? 'bg-green-500/5' : ''}`}
                        onClick={() => onTogglePlan(g.key)}
                      >
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={checked} readOnly className="pointer-events-none" />
                        </td>
                        <td className="px-2 py-1.5 truncate max-w-md" title={g.update}>{g.update || '-'}</td>
                        <td className="px-2 py-1.5 truncate max-w-[140px]" title={g.vendor}>{g.vendor}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{g.deviceCount}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{g.cveCount}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] border ${SEV_PILL[g.topSev]}`}>{g.topSev}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreKpiCard({ value, label, suffix, hint, gradient, onClick }: { value: number | null; label: string; suffix: string; hint: string; gradient: string; onClick?: () => void }) {
  const display = value === null || !Number.isFinite(value) ? '-' : (suffix === '%' ? value.toFixed(1) : value.toFixed(1));
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-md p-3 shadow text-center overflow-hidden text-white w-full transition-transform ${onClick ? 'cursor-pointer hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-blue-400/60' : 'cursor-default'}`}
      style={{ background: gradient }}
      title={onClick ? `Open ${label} details` : undefined}
    >
      <div className="text-2xl font-extrabold leading-none">{display}<span className="text-xs font-semibold opacity-80 ml-0.5">{value === null ? '' : suffix}</span></div>
      <div className="text-[10px] uppercase tracking-wider mt-1.5 opacity-90">{label}</div>
      <div className="text-[9px] mt-0.5 opacity-70">{hint}</div>
    </button>
  );
}

function PctKpiCard({ value, label, gradient, onClick }: { value: number | null; label: string; gradient: string; onClick?: () => void }) {
  const display = value === null || !Number.isFinite(value) ? '-' : `${value}%`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-md p-3 shadow text-center overflow-hidden text-white w-full transition-transform ${onClick ? 'cursor-pointer hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-blue-400/60' : 'cursor-default'}`}
      style={{ background: gradient }}
      title={onClick ? `Open ${label} details` : undefined}
    >
      <div className="text-2xl font-extrabold leading-none">{display}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1.5 opacity-90">{label}</div>
    </button>
  );
}

function DaysKpiCard({ value, label, gradient, onClick }: { value: number | null; label: string; gradient: string; onClick?: () => void }) {
  const display = value === null || !Number.isFinite(value) ? '-' : `${value}d`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-md p-3 shadow text-center overflow-hidden text-white w-full transition-transform ${onClick ? 'cursor-pointer hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-blue-400/60' : 'cursor-default'}`}
      style={{ background: gradient }}
      title={onClick ? `Open ${label} details` : undefined}
    >
      <div className="text-2xl font-extrabold leading-none">{display}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1.5 opacity-90">{label}</div>
    </button>
  );
}

function NoDataNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-2.5 text-[11px] text-muted-foreground italic">
      <span className="font-semibold not-italic mr-1">Note:</span>{children}
    </div>
  );
}

function SeverityDonut({ sev }: { sev: { Critical: number; High: number; Medium: number; Low: number } }) {
  const total = sev.Critical + sev.High + sev.Medium + sev.Low;
  if (total === 0) return <div className="text-xs text-muted-foreground py-6 text-center">No severity data.</div>;
  const slices: { key: keyof typeof sev; color: string }[] = [
    { key: 'Critical', color: '#c92a2a' },
    { key: 'High', color: '#fd7e14' },
    { key: 'Medium', color: '#fcc419' },
    { key: 'Low', color: '#51cf66' },
  ];
  const r = 60, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="flex items-center gap-4">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <g transform="translate(80,80) rotate(-90)">
          <circle r={r} fill="none" stroke="rgba(120,120,120,0.15)" strokeWidth="22" />
          {slices.map((s) => {
            const v = sev[s.key];
            if (v === 0) return null;
            const len = (v / total) * c;
            const el = (
              <circle
                key={s.key}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth="22"
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        <text x="80" y="76" textAnchor="middle" className="fill-foreground" style={{ fontSize: 18, fontWeight: 700 }}>{total.toLocaleString()}</text>
        <text x="80" y="92" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 10 }}>CVEs</text>
      </svg>
      <div className="space-y-1.5 text-xs">
        {slices.map((s) => {
          const v = sev[s.key];
          const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
          return (
            <div key={s.key} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
              <span className="font-semibold w-16">{s.key}</span>
              <span className="font-mono">{v.toLocaleString()}</span>
              <span className="text-muted-foreground text-[10px]">({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SeverityBars({ sev }: { sev: { Critical: number; High: number; Medium: number; Low: number } }) {
  const max = Math.max(1, sev.Critical, sev.High, sev.Medium, sev.Low);
  const rows: { k: keyof typeof sev; color: string }[] = [
    { k: 'Critical', color: 'bg-red-500' },
    { k: 'High', color: 'bg-orange-500' },
    { k: 'Medium', color: 'bg-amber-400' },
    { k: 'Low', color: 'bg-green-500' },
  ];
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.k} className="flex items-center gap-2 text-[11px]">
          <div className="w-16 text-muted-foreground">{r.k}</div>
          <div className="flex-1 bg-muted/30 rounded h-4 overflow-hidden border border-border">
            <div className={`h-full ${r.color}`} style={{ width: `${(sev[r.k] / max) * 100}%` }} />
          </div>
          <div className="w-14 text-right font-mono font-semibold text-foreground">{sev[r.k].toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function OsDistributionChart({ data }: { data: { os: string; count: number }[] }) {
  if (data.length === 0) return <div className="text-xs text-muted-foreground py-6 text-center">No OS data.</div>;
  const max = Math.max(1, ...data.map((d) => d.count));
  const palette = ['#2563eb', '#0ea5e9', '#7c3aed', '#a78bfa', '#10b981', '#f59e0b', '#ef4444', '#64748b'];
  return (
    <div className="space-y-1.5">
      {data.slice(0, 10).map((d, i) => (
        <div key={d.os} className="flex items-center gap-2 text-[11px]">
          <div className="w-44 truncate text-muted-foreground" title={d.os}>{d.os}</div>
          <div className="flex-1 bg-muted/30 rounded h-4 overflow-hidden border border-border">
            <div className="h-full" style={{ width: `${(d.count / max) * 100}%`, background: palette[i % palette.length] }} />
          </div>
          <div className="w-12 text-right font-mono font-semibold text-foreground">{d.count.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function TopRecommendationsTable({ rows }: { rows: TopRecommendation[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left">Recommendation</th>
            <th className="px-2 py-1.5 text-left">Product</th>
            <th className="px-2 py-1.5 text-right">Exposed</th>
            <th className="px-2 py-1.5 text-right">Sev Score</th>
            <th className="px-2 py-1.5 text-left">Type</th>
            <th className="px-2 py-1.5 text-left">Status</th>
            <th className="px-2 py-1.5 text-left">First Detected</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/50 hover:bg-muted/30">
              <td className="px-2 py-1.5">
                <div className="font-semibold text-foreground truncate max-w-md" title={r.title}>{r.title}</div>
                {r.vendor && <div className="text-[10px] text-muted-foreground">{r.vendor}</div>}
              </td>
              <td className="px-2 py-1.5 truncate max-w-[160px]" title={r.product}>{r.product || '-'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{r.exposedMachines.toLocaleString()}</td>
              <td className="px-2 py-1.5 text-right font-mono font-semibold" style={{ color: r.severityScore >= 9 ? '#c92a2a' : r.severityScore >= 7 ? '#d9480f' : '#666' }}>
                {r.severityScore.toFixed(1)}
              </td>
              <td className="px-2 py-1.5">{r.remediationType || '-'}</td>
              <td className="px-2 py-1.5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${r.status?.toLowerCase() === 'active' ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200' : 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200'}`}>
                  {r.status || 'Unknown'}
                </span>
              </td>
              <td className="px-2 py-1.5 text-muted-foreground">{r.firstDetected ? r.firstDetected.slice(0, 10) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopSoftwareTable({ rows }: { rows: TopSoftware[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left">Software</th>
            <th className="px-2 py-1.5 text-left">Vendor</th>
            <th className="px-2 py-1.5 text-right">Weaknesses</th>
            <th className="px-2 py-1.5 text-right">Exposed</th>
            <th className="px-2 py-1.5 text-right">Installs</th>
            <th className="px-2 py-1.5 text-left">Support</th>
            <th className="px-2 py-1.5 text-left">Exploit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="border-t border-border/50 hover:bg-muted/30">
              <td className="px-2 py-1.5 font-semibold text-foreground truncate max-w-[220px]" title={s.name}>{s.name || '-'}</td>
              <td className="px-2 py-1.5 truncate max-w-[140px] text-muted-foreground" title={s.vendor}>{s.vendor || '-'}</td>
              <td className="px-2 py-1.5 text-right font-mono font-semibold text-orange-700 dark:text-orange-300">{s.weaknesses.toLocaleString()}</td>
              <td className="px-2 py-1.5 text-right font-mono">{s.exposedMachines.toLocaleString()}</td>
              <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">{s.installations.toLocaleString()}</td>
              <td className="px-2 py-1.5">
                {s.endOfSupportStatus === 'EOS'
                  ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-600 text-white font-bold">EOL</span>
                  : s.endOfSupportStatus && s.endOfSupportStatus !== 'None'
                    ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{s.endOfSupportStatus}</span>
                    : <span className="text-muted-foreground">-</span>}
              </td>
              <td className="px-2 py-1.5">
                {s.publicExploit
                  ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-200">Public</span>
                  : <span className="text-muted-foreground text-[10px]">-</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Exploit Maturity Score (0-5). Distinctive at-a-glance indicator
 * derived from Defender Threat Intel signals already on the threat row.
 * Higher = closer to weaponized + actively used. Pure function, no
 * fabricated data: every input is a real flag from /vulnerability/summary.
 *
 *   +2  exploitInWild (Microsoft observed in-the-wild exploitation)
 *   +1  publicExploit (PoC available to the public)
 *   +1  KEV listed by CISA (exploitTypes contains kev/cisa)
 *   +1  ransomware family linkage (exploitTypes contains ransom)
 *
 * Capped at 5. A score of 0 means no known exploitation signal in
 * Defender Threat Intel, even though severity may still be Critical.
 */
function exploitMaturity(t: TopThreat): { score: number; label: string; color: string } {
  let s = 0;
  if (t.exploitInWild) s += 2;
  if (t.publicExploit) s += 1;
  if (t.exploitTypes.some((x) => /kev|cisa/i.test(x))) s += 1;
  if (t.exploitTypes.some((x) => /ransom/i.test(x))) s += 1;
  s = Math.min(5, s);
  const label = s === 0 ? 'No known exploit'
    : s === 1 ? 'PoC available'
    : s === 2 ? 'In wild'
    : s === 3 ? 'In wild, public PoC'
    : s === 4 ? 'KEV + in wild'
    : 'Weaponized';
  const color = s >= 4 ? '#7f1d1d' : s === 3 ? '#c92a2a' : s === 2 ? '#d9480f' : s === 1 ? '#e67700' : '#6b7280';
  return { score: s, label, color };
}

function ExploitMaturityBadge({ t }: { t: TopThreat }) {
  const m = exploitMaturity(t);
  return (
    <div className="flex items-center gap-1.5" title={`${m.label} (Defender TI signals)`}>
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="w-1.5 h-3.5 rounded-sm"
            style={{ background: i < m.score ? m.color : 'rgba(120,120,120,0.18)' }}
          />
        ))}
      </div>
      <span className="text-[10px] font-mono font-semibold" style={{ color: m.color }}>{m.score}/5</span>
    </div>
  );
}

function TopThreatsTable({ rows, attackMap, onMapAll, canMapAttack }: { rows: TopThreat[]; attackMap: AttackMap; onMapAll: () => Promise<void>; canMapAttack: boolean }) {
  const [busy, setBusy] = useState(false);
  const uncovered = rows.filter((r) => !attackMap[r.cveId.toUpperCase()]).length;
  const handle = async () => { if (!canMapAttack) return; setBusy(true); try { await onMapAll(); } finally { setBusy(false); } };
  // Weaponized count (maturity >= 4): the headline figure that makes this
  // dashboard actually different from a stock CVE list.
  const weaponized = rows.filter((r) => exploitMaturity(r).score >= 4).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-muted-foreground">
          <span className="font-bold text-red-700 dark:text-red-300">{weaponized}</span> of {rows.length} threats are weaponized (Exploit Maturity 4 or higher). PoC column links to GitHub code search so analysts can pivot straight to public proof-of-concept repos.
        </div>
        <button
          onClick={handle}
          disabled={busy || uncovered === 0 || !canMapAttack}
          className="text-[10px] font-semibold px-2.5 py-1 rounded border border-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 disabled:opacity-50"
        >
          {busy ? 'Mapping...' : uncovered === 0 ? 'ATT&CK mapped' : `Map ATT&CK (${uncovered})`}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">CVE</th>
              <th className="px-2 py-1.5 text-left">Severity</th>
              <th className="px-2 py-1.5 text-right">CVSS</th>
              <th className="px-2 py-1.5 text-right">Exposed</th>
              <th className="px-2 py-1.5 text-left">Exploit Maturity</th>
              <th className="px-2 py-1.5 text-left">Intelligence</th>
              <th className="px-2 py-1.5 text-left">PoC</th>
              <th className="px-2 py-1.5 text-left">ATT&CK</th>
              <th className="px-2 py-1.5 text-left">Published</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const sevKey = (['Critical', 'High', 'Medium', 'Low'].includes(t.severity) ? t.severity : 'Unknown') as Severity;
              const pocUrl = `https://github.com/search?q=${encodeURIComponent(t.cveId)}&type=repositories&s=stars&o=desc`;
              const pocCodeUrl = `https://github.com/search?q=${encodeURIComponent(t.cveId)}&type=code`;
              const nomiUrl = `https://github.com/nomi-sec/PoC-in-GitHub/blob/master/${t.cveId.toUpperCase().split('-')[1]}/${t.cveId.toUpperCase()}.json`;
              return (
                <tr key={t.cveId} className="border-t border-border/50">
                  <td className="px-2 py-1.5 font-mono">
                    <a href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(t.cveId)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{t.cveId}</a>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] border ${SEV_PILL[sevKey]}`}>{t.severity}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{t.cvss.toFixed(1)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{t.exposedMachines.toLocaleString()}</td>
                  <td className="px-2 py-1.5">
                    <ExploitMaturityBadge t={t} />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {t.exploitInWild && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-600 text-white font-bold">In Wild</span>}
                      {t.publicExploit && <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">Public</span>}
                      {t.exploitTypes.some((x) => /kev|cisa/i.test(x)) && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-200">KEV</span>}
                      {t.exploitTypes.some((x) => /ransom/i.test(x)) && <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-600 text-white font-bold">Ransomware</span>}
                      {!t.exploitInWild && !t.publicExploit && t.exploitTypes.length === 0 && <span className="text-[10px] text-muted-foreground">No signal</span>}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <a
                        href={pocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Search GitHub repositories for this CVE"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-slate-400/40 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                      >
                        Repos
                      </a>
                      <a
                        href={pocCodeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Search GitHub code (often surfaces PoC scripts inline)"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-slate-400/40 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                      >
                        Code
                      </a>
                      {t.publicExploit && (
                        <a
                          href={nomiUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="nomi-sec/PoC-in-GitHub curated index entry for this CVE"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-purple-400/40 bg-purple-100 text-purple-900 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:hover:bg-purple-900/60"
                        >
                          Curated
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <AttackChips techniques={attackMap[t.cveId.toUpperCase()]} />
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{t.publishedOn ? t.publishedOn.slice(0, 10) : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FiltersBar({
  search, setSearch, sevF, setSevF, expF, setExpF, osF, setOsF, grpF, setGrpF,
  osOptions, grpOptions, onReset,
}: {
  search: string; setSearch: (v: string) => void;
  sevF: string; setSevF: (v: string) => void;
  expF: string; setExpF: (v: string) => void;
  osF: string; setOsF: (v: string) => void;
  grpF: string; setGrpF: (v: string) => void;
  osOptions: string[]; grpOptions: string[];
  onReset: () => void;
}) {
  return (
    <div className="bg-card rounded-xl shadow-md p-3 border border-border">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <FilterField label="Search">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="CVE, device, software..." className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background" />
        </FilterField>
        <FilterField label="Severity">
          <select value={sevF} onChange={(e) => setSevF(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background">
            <option value="">All</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </FilterField>
        <FilterField label="Exploit">
          <select value={expF} onChange={(e) => setExpF(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background">
            <option value="">All</option>
            <option value="ExploitIsInKit">In Kit</option>
            <option value="ExploitIsVerified">Verified</option>
            <option value="ExploitIsPublic">Public</option>
            <option value="NoExploit">No Exploit</option>
          </select>
        </FilterField>
        <FilterField label="OS">
          <select value={osF} onChange={(e) => setOsF(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background">
            <option value="">All</option>
            {osOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </FilterField>
        <FilterField label="Group">
          <select value={grpF} onChange={(e) => setGrpF(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border border-border bg-background">
            <option value="">All</option>
            {grpOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </FilterField>
        <FilterField label=" ">
          <button onClick={onReset} className="w-full text-xs font-semibold px-2 py-1.5 rounded border border-border bg-muted/30 hover:bg-muted/60">Reset</button>
        </FilterField>
      </div>
    </div>
  );
}
